/**
 * Pagina de Configuracao de Integracoes do Tenant
 * F-038: Cards para PIX, WhatsApp, AssineMais, NFSe
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Input, useToast,
} from "@payflow/ui";

const API_BASE = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "payflow_token";

interface PixConfig {
  provider: "cora" | "asaas" | "none";
  ativo: boolean;
  clientId?: string;
  clientSecret?: string;
  apiKey?: string;
  ambiente?: "homologacao" | "producao";
}

interface WhatsappConfig {
  provider: "menuia" | "none";
  ativo: boolean;
  authKey?: string;
  baseUrl?: string;
  deviceId?: string;
}

interface AssineMaisConfig {
  ativo: boolean;
  apiKey?: string;
  canalEnvio?: "EMAIL" | "WHATSAPP";
}

interface NfseConfig {
  provider: "focus" | "govbr" | "none";
  ativo: boolean;
  token?: string;
  ambiente?: "homologacao" | "producao";
  certificadoPath?: string;
  certificadoSenha?: string;
  prestador?: {
    cnpj?: string;
    inscricaoMunicipal?: string;
    codigoMunicipio?: string;
  };
  servico?: {
    codigoTributario?: string;
    cnae?: string;
    discriminacao?: string;
    aliquotaIss?: number;
  };
}

const DEFAULT_PIX: PixConfig = { provider: "none", ativo: false };
const DEFAULT_WHATSAPP: WhatsappConfig = { provider: "none", ativo: false };
const DEFAULT_ASSINEMAIS: AssineMaisConfig = { ativo: false, canalEnvio: "WHATSAPP" };
const DEFAULT_NFSE: NfseConfig = { provider: "none", ativo: false };

export function SettingsIntegrationsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const webhookUrl = `${API_BASE}/v1/webhook/assinemais`;

  const [pixConfig, setPixConfig] = useState<PixConfig>({ ...DEFAULT_PIX });
  const [whatsappConfig, setWhatsappConfig] = useState<WhatsappConfig>({ ...DEFAULT_WHATSAPP });
  const [assineMaisConfig, setAssineMaisConfig] = useState<AssineMaisConfig>({ ...DEFAULT_ASSINEMAIS });
  const [nfseConfig, setNfseConfig] = useState<NfseConfig>({ ...DEFAULT_NFSE });

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
  });

  // ============ Fetch ============

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/admin/settings/integrations`, {
        headers: getHeaders(),
      });
      const json = await res.json();
      if (json.success && json.data) {
        if (json.data.pixConfig) setPixConfig({ ...DEFAULT_PIX, ...json.data.pixConfig });
        if (json.data.whatsappConfig) setWhatsappConfig({ ...DEFAULT_WHATSAPP, ...json.data.whatsappConfig });
        if (json.data.assineMaisConfig) setAssineMaisConfig({ ...DEFAULT_ASSINEMAIS, ...json.data.assineMaisConfig });
        if (json.data.nfseConfig) setNfseConfig({ ...DEFAULT_NFSE, ...json.data.nfseConfig });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // ============ Save ============

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const saveIntegration = async (key: string, data: any) => {
    setSaving(key);
    try {
      const res = await fetch(`${API_BASE}/v1/admin/settings/integrations`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ [key]: data }),
      });
      const json = await res.json();
      if (json.success) {
        toast({ variant: "success", title: t("integrations.save_success") });
      } else {
        toast({ variant: "error", title: json.error?.message || t("integrations.save_error") });
      }
    } catch {
      toast({ variant: "error", title: t("common.connection_error") });
    } finally {
      setSaving(null);
    }
  };

  // ============ Render ============

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        {t("integrations.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t("integrations.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("integrations.description")}
        </p>
      </div>

      {/* PIX */}
      <IntegrationCard
        title="PIX"
        description="Gateway de pagamento PIX para receber parcelas"
        icon={
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
          </svg>
        }
        ativo={pixConfig.ativo}
        onToggle={(v) => setPixConfig({ ...pixConfig, ativo: v })}
        saving={saving === "pixConfig"}
        onSave={() => saveIntegration("pixConfig", pixConfig)}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Provedor</label>
            <select
              value={pixConfig.provider}
              onChange={(e) => setPixConfig({ ...pixConfig, provider: e.target.value as PixConfig["provider"] })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="none">Nenhum</option>
              <option value="cora">Cora</option>
              <option value="asaas">Asaas</option>
            </select>
          </div>

          {pixConfig.provider === "cora" && (
            <>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Ambiente</label>
                <select
                  value={pixConfig.ambiente || "homologacao"}
                  onChange={(e) => setPixConfig({ ...pixConfig, ambiente: e.target.value as "homologacao" | "producao" })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="homologacao">Homologacao</option>
                  <option value="producao">Producao</option>
                </select>
              </div>
              <Input
                label="Client ID"
                value={pixConfig.clientId || ""}
                onChange={(e) => setPixConfig({ ...pixConfig, clientId: e.target.value })}
                placeholder="Cora Client ID"
              />
              <Input
                label="Client Secret"
                type="password"
                value={pixConfig.clientSecret || ""}
                onChange={(e) => setPixConfig({ ...pixConfig, clientSecret: e.target.value })}
                placeholder="Cora Client Secret"
              />
            </>
          )}

          {pixConfig.provider === "asaas" && (
            <>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Ambiente</label>
                <select
                  value={pixConfig.ambiente || "homologacao"}
                  onChange={(e) => setPixConfig({ ...pixConfig, ambiente: e.target.value as "homologacao" | "producao" })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="homologacao">Homologacao</option>
                  <option value="producao">Producao</option>
                </select>
              </div>
              <Input
                label="API Key"
                type="password"
                value={pixConfig.apiKey || ""}
                onChange={(e) => setPixConfig({ ...pixConfig, apiKey: e.target.value })}
                placeholder="Asaas API Key"
              />
            </>
          )}
        </div>
      </IntegrationCard>

      {/* WhatsApp */}
      <IntegrationCard
        title="WhatsApp"
        description="Envio de mensagens automaticas via WhatsApp"
        icon={
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
          </svg>
        }
        ativo={whatsappConfig.ativo}
        onToggle={(v) => setWhatsappConfig({ ...whatsappConfig, ativo: v })}
        saving={saving === "whatsappConfig"}
        onSave={() => saveIntegration("whatsappConfig", whatsappConfig)}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Provedor</label>
            <select
              value={whatsappConfig.provider}
              onChange={(e) => setWhatsappConfig({ ...whatsappConfig, provider: e.target.value as WhatsappConfig["provider"] })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="none">Nenhum</option>
              <option value="menuia">MenuIA</option>
            </select>
          </div>

          {whatsappConfig.provider === "menuia" && (
            <>
              <Input
                label="Auth Key"
                type="password"
                value={whatsappConfig.authKey || ""}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, authKey: e.target.value })}
                placeholder="MenuIA Auth Key"
              />
              <Input
                label="Base URL"
                value={whatsappConfig.baseUrl || ""}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, baseUrl: e.target.value })}
                placeholder="https://chatbot.menuia.com"
              />
              <Input
                label="Device ID"
                value={whatsappConfig.deviceId || ""}
                onChange={(e) => setWhatsappConfig({ ...whatsappConfig, deviceId: e.target.value })}
                placeholder="ID do dispositivo"
              />
            </>
          )}
        </div>
      </IntegrationCard>

      {/* AssineMais */}
      <IntegrationCard
        title="AssineMais"
        description="Assinatura digital de contratos"
        icon={
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
          </svg>
        }
        ativo={assineMaisConfig.ativo}
        onToggle={(v) => setAssineMaisConfig({ ...assineMaisConfig, ativo: v })}
        saving={saving === "assineMaisConfig"}
        onSave={() => saveIntegration("assineMaisConfig", assineMaisConfig)}
      >
        <div className="space-y-4">
          <Input
            label="API Key"
            type="password"
            value={assineMaisConfig.apiKey || ""}
            onChange={(e) => setAssineMaisConfig({ ...assineMaisConfig, apiKey: e.target.value })}
            placeholder="AssineMais API Key"
          />
          <div>
            <label className="mb-1 block text-sm font-medium">
              {t("integrations.assinemais_channel")}
            </label>
            <select
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={assineMaisConfig.canalEnvio || "WHATSAPP"}
              onChange={(e) =>
                setAssineMaisConfig({
                  ...assineMaisConfig,
                  canalEnvio: e.target.value as "EMAIL" | "WHATSAPP",
                })
              }
            >
              <option value="WHATSAPP">{t("integrations.channel_whatsapp")}</option>
              <option value="EMAIL">{t("integrations.channel_email")}</option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("integrations.assinemais_channel_hint")}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">
              {t("integrations.assinemais_webhook")}
            </label>
            <div className="flex gap-2">
              <Input
                value={webhookUrl}
                readOnly
                onFocus={(e) => e.target.select()}
                className="font-mono text-xs"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(webhookUrl);
                  toast({ variant: "success", title: t("integrations.webhook_copied") });
                }}
              >
                {t("common.copy")}
              </Button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("integrations.assinemais_webhook_hint")}
            </p>
          </div>
        </div>
      </IntegrationCard>

      {/* NFSe */}
      <IntegrationCard
        title="NFSe"
        description="Emissao de Nota Fiscal de Servico Eletronica"
        icon={
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
          </svg>
        }
        ativo={nfseConfig.ativo}
        onToggle={(v) => setNfseConfig({ ...nfseConfig, ativo: v })}
        saving={saving === "nfseConfig"}
        onSave={() => saveIntegration("nfseConfig", nfseConfig)}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Provedor</label>
            <select
              value={nfseConfig.provider}
              onChange={(e) => setNfseConfig({ ...nfseConfig, provider: e.target.value as NfseConfig["provider"] })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="none">Nenhum</option>
              <option value="focus">Focus NFe</option>
              <option value="govbr">Gov.br (Nacional)</option>
            </select>
          </div>

          {nfseConfig.provider !== "none" && (
            <>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Ambiente</label>
                <select
                  value={nfseConfig.ambiente || "homologacao"}
                  onChange={(e) => setNfseConfig({ ...nfseConfig, ambiente: e.target.value as "homologacao" | "producao" })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="homologacao">Homologacao</option>
                  <option value="producao">Producao</option>
                </select>
              </div>

              {nfseConfig.provider === "focus" && (
                <Input
                  label="Token Focus NFe"
                  type="password"
                  value={nfseConfig.token || ""}
                  onChange={(e) => setNfseConfig({ ...nfseConfig, token: e.target.value })}
                  placeholder="Token da API"
                />
              )}

              {nfseConfig.provider === "govbr" && (
                <>
                  <Input
                    label="Caminho do certificado (.pfx)"
                    value={nfseConfig.certificadoPath || ""}
                    onChange={(e) => setNfseConfig({ ...nfseConfig, certificadoPath: e.target.value })}
                    placeholder="/path/to/certificado.pfx"
                  />
                  <Input
                    label="Senha do certificado"
                    type="password"
                    value={nfseConfig.certificadoSenha || ""}
                    onChange={(e) => setNfseConfig({ ...nfseConfig, certificadoSenha: e.target.value })}
                    placeholder="Senha"
                  />
                </>
              )}

              {/* Prestador */}
              <div>
                <h4 className="mb-2 text-sm font-medium text-foreground">{t("integrations.section_prestador")}</h4>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Input
                    label="CNPJ"
                    value={nfseConfig.prestador?.cnpj || ""}
                    onChange={(e) =>
                      setNfseConfig({
                        ...nfseConfig,
                        prestador: { ...nfseConfig.prestador, cnpj: e.target.value },
                      })
                    }
                    placeholder="00.000.000/0000-00"
                  />
                  <Input
                    label="Inscricao Municipal"
                    value={nfseConfig.prestador?.inscricaoMunicipal || ""}
                    onChange={(e) =>
                      setNfseConfig({
                        ...nfseConfig,
                        prestador: { ...nfseConfig.prestador, inscricaoMunicipal: e.target.value },
                      })
                    }
                  />
                  <Input
                    label="Codigo Municipio (IBGE)"
                    value={nfseConfig.prestador?.codigoMunicipio || ""}
                    onChange={(e) =>
                      setNfseConfig({
                        ...nfseConfig,
                        prestador: { ...nfseConfig.prestador, codigoMunicipio: e.target.value },
                      })
                    }
                  />
                </div>
              </div>

              {/* Servico */}
              <div>
                <h4 className="mb-2 text-sm font-medium text-foreground">{t("integrations.section_servico")}</h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    label="Codigo Tributario"
                    value={nfseConfig.servico?.codigoTributario || ""}
                    onChange={(e) =>
                      setNfseConfig({
                        ...nfseConfig,
                        servico: { ...nfseConfig.servico, codigoTributario: e.target.value },
                      })
                    }
                    placeholder="170101"
                  />
                  <Input
                    label="CNAE"
                    value={nfseConfig.servico?.cnae || ""}
                    onChange={(e) =>
                      setNfseConfig({
                        ...nfseConfig,
                        servico: { ...nfseConfig.servico, cnae: e.target.value },
                      })
                    }
                    placeholder="7020400"
                  />
                  <Input
                    label="Discriminacao"
                    value={nfseConfig.servico?.discriminacao || ""}
                    onChange={(e) =>
                      setNfseConfig({
                        ...nfseConfig,
                        servico: { ...nfseConfig.servico, discriminacao: e.target.value },
                      })
                    }
                    placeholder="Servicos prestados no mes."
                  />
                  <Input
                    label="Aliquota ISS (%)"
                    type="number"
                    value={nfseConfig.servico?.aliquotaIss ?? ""}
                    onChange={(e) =>
                      setNfseConfig({
                        ...nfseConfig,
                        servico: { ...nfseConfig.servico, aliquotaIss: Number(e.target.value) },
                      })
                    }
                    placeholder="5"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </IntegrationCard>
    </div>
  );
}

// ============ Integration Card Component ============

function IntegrationCard({
  title,
  description,
  icon,
  ativo,
  onToggle,
  saving,
  onSave,
  children,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  ativo: boolean;
  onToggle: (v: boolean) => void;
  saving: boolean;
  onSave: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
              ativo ? "bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400" : "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500"
            }`}>
              {icon}
            </div>
            <div>
              <CardTitle className="text-base">{title}</CardTitle>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
          <ToggleSwitch checked={ativo} onChange={onToggle} />
        </div>
      </CardHeader>
      {ativo && (
        <CardContent className="space-y-4 border-t border-border pt-4">
          {children}
          <div className="flex justify-end pt-2">
            <Button onClick={onSave} disabled={saving} size="sm">
              {saving ? t("integrations.button_saving") : t("integrations.button_save")}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ============ Toggle Switch ============

function ToggleSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 cursor-pointer rounded-full transition-colors ${
        checked ? "bg-primary-500" : "bg-gray-300 dark:bg-gray-600"
      }`}
    >
      <div
        className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : ""
        }`}
      />
    </div>
  );
}
